import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { apiRequest, getStoredToken, setStoredToken, ApiError } from '@/api/client'
import { useLearnerMe } from '@/composables/useLearnerMe'

interface WhoamiResponse {
  user_id: string
  email: string
  org_id: string
  role: string
}

interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(getStoredToken())
  const user = ref<WhoamiResponse | null>(null)
  const loading = ref(false)
  const bootstrapping = ref(false)

  const isAuthenticated = computed(() => token.value !== null && user.value !== null)

  async function login(email: string, password: string): Promise<void> {
    loading.value = true
    try {
      const res = await apiRequest<TokenResponse>('/v1/auth/token', {
        method: 'POST',
        body: { email, password },
        auth: false
      })
      setStoredToken(res.access_token)
      token.value = res.access_token
      user.value = await apiRequest<WhoamiResponse>('/v1/auth/whoami')
    } finally {
      loading.value = false
    }
  }

  function logout(): void {
    setStoredToken(null)
    token.value = null
    user.value = null
    useLearnerMe().reset()
  }

  async function bootstrap(): Promise<void> {
    if (!token.value) return
    bootstrapping.value = true
    try {
      user.value = await apiRequest<WhoamiResponse>('/v1/auth/whoami')
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        logout()
      }
    } finally {
      bootstrapping.value = false
    }
  }

  /** Reconcile in-memory auth with localStorage. The API client clears the
   * stored token on any 401 (e.g. the 12h JWT expiring mid-session), but it
   * can't reach this store — so without this sync the router guard keeps
   * seeing a stale in-memory token and never punts to /login until a hard
   * reload. Called by the router guard on every navigation. */
  function syncFromStorage(): void {
    const stored = getStoredToken()
    if (token.value && !stored) {
      token.value = null
      user.value = null
      useLearnerMe().reset()
    }
  }

  return { token, user, loading, bootstrapping, isAuthenticated, login, logout, bootstrap, syncFromStorage }
})
